/**
 * 직원 관리 — 백엔드 `app/accounts/staff_router.py` 의 짝. 관리자만.
 *
 * 막는 규칙도 같이 흉내 낸다. 재직 중인 관리자가 0명이 되는 변경과 자기 역할 변경을 거부하고,
 * 퇴사로 바꿀 때 퇴사일을 요구하고, 급여는 덮어쓰지 않고 앞 기간을 닫는다. 직원 항목 값은 종류에
 * 맞는지 보고, 선택지에서 빠진 값은 비운다.
 */
import { HttpResponse, http } from "msw";
import { today } from "@/lib/dates";
import type {
  Employment,
  Leave,
  LeaveInput,
  Salary,
  SalaryInput,
  Staff,
  StaffCreateInput,
  StaffField,
  StaffFieldInput,
  StaffFieldUpdateInput,
  StaffUpdateInput,
} from "@/types";
import { hourlyCost } from "../data/staff";
import { db } from "../db";
import { fail, latency, requireAdmin, requireHr, requireUser } from "../http";

const BASE = "/api/v1/admin/staff";
const FIELDS = "/api/v1/admin/staff-fields";
const VALUE_INVALID = "직원 항목 값이 항목 종류에 맞지 않습니다.";
const MIN_PASSWORD = 10;
const MAX_PASSWORD = 128;

const LAST_ADMIN =
  "관리자는 사무소에 한 명이라 비울 수 없습니다. 관리자를 넘기려면 백오피스에서 바꿔 주세요.";

/** 관리자만 바꾸는 칸 — 서버의 ADMIN_ONLY_CHANGES 와 같다. */
const ADMIN_ONLY = ["role", "is_hr"] as const;

const found = (id: string | readonly string[] | undefined) =>
  db.users.find((user) => user.id === id);

/** 그 사람을 뺀 재직 중인 관리자 수. 0 이면 마지막 관리자다. */
const otherAdmins = (id: string) =>
  db.users.filter((user) => user.id !== id && user.role === "admin" && user.status !== "resigned")
    .length;

/** 종류에 맞게 고쳐 담는다. 비우면 null, 맞지 않으면 undefined. */
function normalize(field: StaffField, raw: string | null): string | null | undefined {
  const value = (raw ?? "").trim();
  if (!value) return null;
  if (value.length > 500) return undefined;
  switch (field.value_type) {
    case "number":
      return Number.isFinite(Number(value)) ? String(Number(value)) : undefined;
    case "date":
      return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;
    case "select":
      return field.options.includes(value) ? value : undefined;
    case "text":
      return value;
  }
}

/** 서버처럼 개시일 최신순. */
const leavesOf = (userId: string) =>
  db.leaves
    .filter((row) => row.user_id === userId)
    .sort((a, b) => b.started_on.localeCompare(a.started_on));

/** 휴직 기록은 HR 도 고치지만, 직원 정보처럼 관리자 계정은 관리자만 고친다. */
function leaveTarget(id: string): Staff | Response {
  const actor = requireHr();
  if (actor instanceof Response) return actor;
  const user = found(id);
  if (!user) return fail(404, "직원을 찾을 수 없습니다.");
  if (actor.role !== "admin" && user.role === "admin")
    return fail(403, "이 기능을 쓸 권한이 없습니다.");
  return user;
}

/** 서버처럼 오늘이 어느 휴직 기록의 기간 안이면 휴직, 아니면 재직이다. 퇴사자는 그대로 둔다. */
function followLeave(user: Staff) {
  if (user.status === "resigned") return;
  const now = today();
  const onLeave = db.leaves.some(
    (row) =>
      row.user_id === user.id && row.started_on <= now && (!row.ended_on || now <= row.ended_on)
  );
  user.status = onLeave ? "leave" : "active";
}

/** 종료일은 쉬는 마지막 날이다. 같은 사람의 기간은 겹치지 않는다(비운 종료일은 끝없음). */
function leaveProblem(userId: string, input: LeaveInput, exceptId?: string) {
  if (input.ended_on && input.ended_on < input.started_on)
    return fail(422, "휴직 종료일은 개시일보다 앞설 수 없습니다.");
  const end = (value: string | null) => value ?? "9999-12-31";
  const overlaps = db.leaves.some(
    (row) =>
      row.user_id === userId &&
      row.id !== exceptId &&
      row.started_on <= end(input.ended_on) &&
      input.started_on <= end(row.ended_on)
  );
  return overlaps ? fail(409, "같은 직원의 휴직 기간이 겹칩니다.") : null;
}

export const staffHandlers = [
  http.get(`${BASE}/`, async ({ request }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    const includeResigned = new URL(request.url).searchParams.get("include_resigned") === "true";
    const results = db.users
      .filter((user) => includeResigned || user.status !== "resigned")
      .sort((a, b) => a.name.localeCompare(b.name, "ko"));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/`, async ({ request }) => {
    await latency();
    // 계정은 관리자만 만든다(서버와 같다).
    const actor = requireAdmin();
    if (actor instanceof Response) return actor;
    const input = (await request.json()) as StaffCreateInput;
    // 이메일은 로그인 아이디라 비울 수 없다(서버와 같다).
    const email = input.email?.trim().toLowerCase() || null;
    if (!email) return fail(422, "이메일을 적어 주세요.");
    if (!input.name.trim()) return fail(422, "이름을 적어 주세요.");
    if (db.users.some((user) => user.email?.toLowerCase() === email))
      return fail(409, "같은 이메일을 쓰는 계정이 있습니다.");
    if (input.role === "admin" && otherAdmins(""))
      return fail(409, "관리자는 사무소에 한 명만 둘 수 있습니다.");
    const created: Staff = {
      id: `u-${crypto.randomUUID().slice(0, 8)}`,
      email,
      name: input.name.trim(),
      role: input.role,
      status: "active",
      hired_on: input.hired_on,
      birth_date: null,
      resigned_on: null,
      standard_daily_hours: null,
      standard_monthly_hours: null,
      is_hr: false,
      has_password: !!input.password,
      created_via_google: false,
      totp_enabled: false,
      avatar_url: null,
      fields: {},
    };
    if (input.password) {
      if (input.password.length < 10) return fail(422, "비밀번호는 10자 이상이어야 합니다.");
      db.passwords[created.id] = input.password;
    }
    db.users.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.get(`${BASE}/:id/`, async ({ params }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    const user = found(params.id);
    return user ? HttpResponse.json(user) : fail(404, "직원을 찾을 수 없습니다.");
  }),

  http.patch(`${BASE}/:id/`, async ({ params, request }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    const user = found(params.id);
    if (!user) return fail(404, "직원을 찾을 수 없습니다.");
    const patch = (await request.json()) as StaffUpdateInput;

    // 역할·표시는 관리자만 바꾸고, HR 은 관리자 계정을 고치지 못한다(값이 그대로인 칸은 막지 않는다).
    // 퇴사 처리와 그 되돌리기는 HR 도 한다(서버와 같다).
    const touched = ADMIN_ONLY.some((key) => patch[key] !== undefined && patch[key] !== user[key]);
    if (actor.role !== "admin" && (user.role === "admin" || touched))
      return fail(403, "이 기능을 쓸 권한이 없습니다.");
    if (patch.email !== undefined && !patch.email?.trim())
      return fail(422, "null로 바꿀 수 없는 필드가 있습니다: email.");
    // 휴직으로 들어가고 휴직에서 재직으로 나오는 것은 휴직 기록이 한다.
    if (
      patch.status !== undefined &&
      patch.status !== user.status &&
      (patch.status === "leave" || (user.status === "leave" && patch.status === "active"))
    )
      return fail(
        422,
        "휴직과 복직은 휴직 기록으로 바뀝니다. 휴직 탭에서 기록을 넣거나 종료일을 채워 주세요."
      );
    if (patch.role !== undefined && patch.role !== user.role) {
      if (user.id === actor.id) return fail(409, "자기 역할은 스스로 바꿀 수 없습니다.");
      if (user.role === "admin" && !otherAdmins(user.id)) return fail(409, LAST_ADMIN);
      if (patch.role === "admin" && otherAdmins(user.id))
        return fail(409, "관리자는 사무소에 한 명만 둘 수 있습니다.");
    }
    if (patch.status === "resigned" && user.status !== "resigned") {
      if (!(patch.resigned_on ?? user.resigned_on))
        return fail(422, "퇴사로 바꾸려면 퇴사일이 필요합니다.");
      if (user.role === "admin" && !otherAdmins(user.id)) return fail(409, LAST_ADMIN);
    }
    if (patch.name !== undefined && !patch.name.trim())
      return fail(422, "null로 바꿀 수 없는 필드가 있습니다: name.");
    const daily = patch.standard_daily_hours;
    if (daily != null && !(daily > 0 && daily <= 24 && daily % 0.5 === 0))
      return fail(422, "하루 기준 근무시간은 0.5 단위여야 합니다.");
    if (patch.standard_monthly_hours != null && patch.standard_monthly_hours <= 0)
      return fail(422, "월 기준 근무시간을 확인해 주세요.");

    const { fields: fieldPatch, ...rest } = patch;
    const nextFields = { ...user.fields };
    for (const [fieldId, raw] of Object.entries(fieldPatch ?? {})) {
      const field = db.staffFields.find((row) => row.id === fieldId);
      if (!field) return fail(404, "직원 항목을 찾을 수 없습니다.");
      const value = normalize(field, raw);
      if (value === undefined) return fail(422, VALUE_INVALID);
      if (value === null) delete nextFields[fieldId];
      else nextFields[fieldId] = value;
    }

    Object.assign(user, rest, { fields: nextFields });
    // 퇴사를 되돌리면 오늘 휴직 중인지 기록으로 다시 본다.
    if (patch.status === "active") followLeave(user);
    return HttpResponse.json(user);
  }),

  /**
   * 관리자 넘기기 — 받는 사람은 관리자, 넘긴 사람은 HR 표시도 꺼진 직원이 된다(서버와 같다).
   * 받는 사람은 재직 중이고 이메일이 있어야 한다.
   */
  http.post(`${BASE}/:id/make-admin/`, async ({ params }) => {
    await latency();
    const actor = requireAdmin();
    if (actor instanceof Response) return actor;
    const user = found(params.id);
    if (!user) return fail(404, "직원을 찾을 수 없습니다.");
    if (user.id === actor.id) return fail(409, "자기 자신에게는 넘길 수 없습니다.");
    if (user.status === "resigned") return fail(409, "퇴사한 사람에게는 넘길 수 없습니다.");
    if (!user.email) return fail(409, "이메일이 없는 사람에게는 넘길 수 없습니다.");
    const self = found(actor.id);
    if (self) Object.assign(self, { role: "staff", is_hr: false });
    user.role = "admin";
    return HttpResponse.json(user);
  }),

  http.post(`${BASE}/:id/password/`, async ({ params, request }) => {
    await latency();
    const actor = requireAdmin();
    if (actor instanceof Response) return actor;
    const user = found(params.id);
    if (!user) return fail(404, "직원을 찾을 수 없습니다.");
    if (user.created_via_google)
      return fail(
        409,
        "Google 로 처음 만들어진 계정이라 비밀번호를 정할 수 없습니다. Google 로 로그인하세요."
      );
    const { password } = (await request.json()) as { password?: string };
    if (!password || password.length < MIN_PASSWORD || password.length > MAX_PASSWORD)
      return fail(422, `비밀번호는 ${MIN_PASSWORD}자 이상 ${MAX_PASSWORD}자 이하여야 합니다.`);
    db.passwords[user.id] = password;
    user.has_password = true;
    return HttpResponse.json(user);
  }),

  http.post(`${BASE}/:id/disable-totp/`, async ({ params }) => {
    await latency();
    const actor = requireAdmin();
    if (actor instanceof Response) return actor;
    const user = found(params.id);
    if (!user) return fail(404, "직원을 찾을 수 없습니다.");
    user.totp_enabled = false;
    return HttpResponse.json(user);
  }),

  http.get(`${BASE}/:id/salaries/`, async ({ params }) => {
    await latency();
    const actor = requireAdmin();
    if (actor instanceof Response) return actor;
    if (!found(params.id)) return fail(404, "직원을 찾을 수 없습니다.");
    const results = db.salaries
      .filter((row) => row.user_id === params.id)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/:id/salaries/`, async ({ params, request }) => {
    await latency();
    const actor = requireAdmin();
    if (actor instanceof Response) return actor;
    const user = found(params.id);
    if (!user) return fail(404, "직원을 찾을 수 없습니다.");
    const input = (await request.json()) as SalaryInput;
    if (!(input.amount >= 0)) return fail(422, "금액을 확인해 주세요.");
    const current = db.salaries
      .filter((row) => row.user_id === user.id && row.effective_to === null)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
    if (current) {
      if (input.effective_from <= current.effective_from)
        return fail(422, "적용 시작일이 지금 급여의 적용 시작일보다 뒤여야 합니다.");
      current.effective_to = input.effective_from;
    }
    const created: Salary & { user_id: string } = {
      id: `sal-${crypto.randomUUID().slice(0, 8)}`,
      user_id: user.id,
      pay_type: input.pay_type,
      amount: input.amount,
      overhead_rate: input.overhead_rate,
      effective_from: input.effective_from,
      effective_to: null,
      created_at: new Date().toISOString(),
      hourly_cost: hourlyCost(input.pay_type, input.amount, input.overhead_rate),
    };
    db.salaries.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.get(`${BASE}/:id/leaves/`, async ({ params }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    if (!found(params.id)) return fail(404, "직원을 찾을 수 없습니다.");
    return HttpResponse.json({ results: leavesOf(String(params.id)) });
  }),

  http.post(`${BASE}/:id/leaves/`, async ({ params, request }) => {
    await latency();
    const user = leaveTarget(String(params.id));
    if (user instanceof Response) return user;
    const input = (await request.json()) as LeaveInput;
    const problem = leaveProblem(user.id, input);
    if (problem) return problem;
    const created: Leave & { user_id: string } = {
      id: `leave-${crypto.randomUUID().slice(0, 8)}`,
      user_id: user.id,
      ...input,
      note: input.note?.trim() ?? "",
      created_at: new Date().toISOString(),
    };
    db.leaves.push(created);
    followLeave(user);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.put(`${BASE}/:id/leaves/:leaveId/`, async ({ params, request }) => {
    await latency();
    const user = leaveTarget(String(params.id));
    if (user instanceof Response) return user;
    const leave = db.leaves.find((row) => row.id === params.leaveId && row.user_id === user.id);
    if (!leave) return fail(404, "휴직 기록을 찾을 수 없습니다.");
    const input = (await request.json()) as LeaveInput;
    const problem = leaveProblem(user.id, input, leave.id);
    if (problem) return problem;
    Object.assign(leave, input, { note: input.note?.trim() ?? "" });
    followLeave(user);
    return HttpResponse.json(leave);
  }),

  http.delete(`${BASE}/:id/leaves/:leaveId/`, async ({ params }) => {
    await latency();
    const user = leaveTarget(String(params.id));
    if (user instanceof Response) return user;
    const index = db.leaves.findIndex(
      (row) => row.id === params.leaveId && row.user_id === user.id
    );
    if (index < 0) return fail(404, "휴직 기록을 찾을 수 없습니다.");
    db.leaves.splice(index, 1);
    followLeave(user);
    return new HttpResponse(null, { status: 204 });
  }),

  http.get("/api/v1/me/employment/", async () => {
    await latency();
    const me = requireUser();
    if (me instanceof Response) return me;
    const user = found(me.id);
    if (!user) return fail(404, "직원을 찾을 수 없습니다.");
    const employment: Employment = {
      profile_fields: db.staffFields
        .filter((field) => field.is_seed)
        .map((field) => ({
          field_id: field.id,
          name: field.name,
          value: user.fields[field.id] ?? null,
        })),
      hired_on: user.hired_on,
      resigned_on: user.resigned_on,
      status: user.status,
      standard_daily_hours: user.standard_daily_hours ?? db.siteConfig.standard_daily_hours,
      standard_monthly_hours: user.standard_monthly_hours ?? db.siteConfig.standard_monthly_hours,
      leaves: leavesOf(user.id),
    };
    return HttpResponse.json(employment);
  }),

  http.get(`${FIELDS}/`, async () => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    return HttpResponse.json({ results: db.staffFields });
  }),

  http.post(`${FIELDS}/`, async ({ request }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    const input = (await request.json()) as StaffFieldInput;
    const name = input.name.trim();
    if (!name) return fail(422, "항목 이름을 적어 주세요.");
    if (db.staffFields.some((row) => row.name === name))
      return fail(409, "같은 이름의 직원 항목이 있습니다.");
    const options = input.value_type === "select" ? input.options.map((o) => o.trim()) : [];
    if (input.value_type === "select" && !options.length)
      return fail(422, "선택 항목에는 선택지가 하나 이상 있어야 합니다.");
    const created: StaffField = {
      id: `sf-${crypto.randomUUID().slice(0, 8)}`,
      name,
      value_type: input.value_type,
      options,
      is_public: !!input.is_public,
      is_seed: false,
    };
    db.staffFields.push(created);
    return HttpResponse.json(created, { status: 201 });
  }),

  http.patch(`${FIELDS}/:id/`, async ({ params, request }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    const field = db.staffFields.find((row) => row.id === params.id);
    if (!field) return fail(404, "직원 항목을 찾을 수 없습니다.");
    const patch = (await request.json()) as StaffFieldUpdateInput;
    if (
      field.is_seed &&
      ((patch.name !== undefined && patch.name.trim() !== field.name) ||
        (patch.is_public !== undefined && patch.is_public !== field.is_public))
    )
      return fail(409, "기본 항목(소속 · 직급)은 지우거나 이름 · 공개 여부를 바꿀 수 없습니다.");
    if (patch.name !== undefined) {
      const name = patch.name.trim();
      if (!name) return fail(422, "null로 바꿀 수 없는 필드가 있습니다: name.");
      if (db.staffFields.some((row) => row.id !== field.id && row.name === name))
        return fail(409, "같은 이름의 직원 항목이 있습니다.");
      field.name = name;
    }
    if (patch.is_public !== undefined) field.is_public = patch.is_public;
    if (patch.options !== undefined && field.value_type === "select") {
      if (!patch.options.length)
        return fail(422, "선택 항목에는 선택지가 하나 이상 있어야 합니다.");
      field.options = patch.options.map((o) => o.trim());
      // 선택지에서 빠진 값을 고른 직원은 그 칸이 비워진다.
      for (const user of db.users)
        if (user.fields[field.id] && !field.options.includes(user.fields[field.id]))
          delete user.fields[field.id];
    }
    return HttpResponse.json(field);
  }),

  http.delete(`${FIELDS}/:id/`, async ({ params }) => {
    await latency();
    const actor = requireHr();
    if (actor instanceof Response) return actor;
    const index = db.staffFields.findIndex((row) => row.id === params.id);
    if (index < 0) return fail(404, "직원 항목을 찾을 수 없습니다.");
    if (db.staffFields[index].is_seed)
      return fail(409, "기본 항목(소속 · 직급)은 지우거나 이름 · 공개 여부를 바꿀 수 없습니다.");
    db.staffFields.splice(index, 1);
    for (const user of db.users) delete user.fields[params.id as string];
    return new HttpResponse(null, { status: 204 });
  }),
];
